import React, { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { useLang } from "../i18n";
import { REVIEWS } from "../data";
import { api } from "../lib/api";
import Reveal from "./Reveal";
import OliveBranch from "./OliveBranch";

export const Reviews = () => {
  const { t, lang } = useLang();
  const fallback = REVIEWS[lang].map((r) => ({ ...r, rating: 5 }));
  const [reviews, setReviews] = useState(fallback);
  const [summary, setSummary] = useState({ rating: 4.9, count: 500 });
  const [isGoogle, setIsGoogle] = useState(false);
  const [mapsUri, setMapsUri] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/reviews")
      .then(({ data }) => {
        if (!active) return;
        if (Array.isArray(data) && data.length) {
          setReviews(
            data.map((r) => ({
              name: r.name,
              text: r.text?.[lang] || r.text?.de || "",
              role: r.role?.[lang] || r.role?.de || "",
              rating: r.rating || 5,
            }))
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [lang]);

  return (
    <section id="reviews" className="py-24 md:py-32 px-5 md:px-10 bg-qs-bg2">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center flex flex-col items-center mb-14">
          <p className="font-outfit text-qs-red tracking-[0.3em] uppercase text-xs mb-4">
            {t.reviews.eyebrow}
          </p>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl text-qs-text">
            {t.reviews.title}
          </h2>
          <OliveBranch className="w-24 text-qs-gold my-5" />
          <div className="flex items-center gap-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={20} className="fill-qs-gold text-qs-gold" />
            ))}
            <span className="font-outfit text-qs-text2 text-sm ml-2">
              {Number(summary.rating).toFixed(1)} / 5.0
            </span>
          </div>
          {isGoogle && (
            <a
              href={mapsUri || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-badge"
              className="font-outfit text-xs text-qs-text2 mt-3 hover:text-qs-red transition-colors"
            >
              {summary.count} {lang === "de" ? "Bewertungen auf Google" : "reviews on Google"}
            </a>
          )}
          <p className="font-outfit text-qs-text2 max-w-xl mt-3">{t.reviews.subtitle}</p>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 90}>
              <div
                data-testid={`review-card-${i}`}
                className="relative bg-qs-bg p-8 h-full border border-qs-border hover:border-qs-gold transition-colors duration-300 hover:-translate-y-1 transform"
              >
                <Quote className="text-qs-gold/40 mb-4" size={36} />
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, s) => (
                    <Star
                      key={s}
                      size={15}
                      className={s < (r.rating || 5) ? "fill-qs-gold text-qs-gold" : "text-qs-border"}
                    />
                  ))}
                </div>
                <p className="font-outfit text-qs-text2 leading-relaxed mb-6 italic line-clamp-6">
                  "{r.text}"
                </p>
                <div className="flex items-center gap-3 mt-auto">
                  {r.photo ? (
                    <img src={r.photo} alt={r.name} className="w-11 h-11 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-qs-red text-white flex items-center justify-center font-cormorant text-lg">
                      {r.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="font-cormorant text-lg text-qs-text leading-none">{r.name}</p>
                    <p className="font-outfit text-xs text-qs-gold tracking-wide uppercase mt-1">
                      {r.role}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
